import type { UserPersona } from '../../types';

/** The magic link verify page. The idle timer pauses here (MDF-258). */
export function isMagicLinkVerifyPath(pathname: string): boolean {
  return pathname === '/auth/verify' || pathname.startsWith('/auth/verify/');
}

/**
 * The link's ?redirect= value as an in-app path, or null. The browser's own
 * URL parser decides, so `//host`, `/\host`, control characters, and dot
 * segments that resolve to another origin are all refused.
 */
export function safeRedirectPath(
  redirect: string | null,
  origin: string
): string | null {
  if (!redirect || !redirect.startsWith('/')) return null;
  try {
    const url = new URL(redirect, origin);
    if (url.origin !== origin) return null;
    const path = `${url.pathname}${url.search}${url.hash}`;
    // Dot segments can normalize to `//host`, which the browser then reads
    // as another origin. Check the path we return, not only the input.
    return new URL(path, origin).origin === origin ? path : null;
  } catch {
    return null;
  }
}

/** The home page for a user's personas, used when a link has no redirect. */
export function personaHomePath(
  personas: Pick<UserPersona, 'persona'>[] | undefined
): string {
  if (!personas) return '/coach';
  const has = (...names: string[]) =>
    personas.some(p => names.includes(p.persona));
  if (has('internal_admin', 'internal_user')) return '/';
  if (has('coach')) return '/coach';
  if (has('participant')) return '/participant';
  if (has('partner')) return '/partner';
  return '/coach';
}

/**
 * MED-1389: the home of a user who is already signed in, for a login page and
 * for `/`. During impersonation the home of the role the admin chose decides,
 * because the user can hold more than one persona and a partner view switch
 * keeps the admin's own user. With no user loaded yet, keep the old default.
 * A user with no persona has no home.
 */
export function signedInHomePath(
  personas: Pick<UserPersona, 'persona'>[] | undefined,
  impersonatedRoleHome: string | null
): string {
  if (impersonatedRoleHome) return impersonatedRoleHome;
  if (!personas) return '/';
  if (personas.length === 0) return '/no-access';
  return personaHomePath(personas);
}

/**
 * MED-1389: the verify page state for a refused link. The API sends a stable
 * code. Its error text for an unknown token says "Invalid or expired", so
 * matching the text showed "Link expired" for a link that never existed.
 */
export function magicLinkErrorState(
  code: string | undefined
): 'expired' | 'used' | 'invalid' {
  if (code === 'expired') return 'expired';
  if (code === 'already_used') return 'used';
  return 'invalid';
}
